import { useEffect, useRef, useState } from 'react'
import { Html } from '@react-three/drei'
import { track } from '@vercel/analytics'
import useGame from '../stores/useGame.jsx'
import * as THREE from 'three'
import { loadPOIData, getPOIDataByNode } from '../utils/poiDataLoader.js'



export function POI_Markers({ nodes }) {
    const [poiGroups, setPoiGroups] = useState([])
    const [poiDataMap, setPoiDataMap] = useState(null)
    const [offsetMap, setOffsetMap] = useState({})
    const poiNodesRef = useRef({})
    const [hoveredIndex, setHoveredIndex] = useState(null)
    const [fadeState, setFadeState] = useState('in') // 'in' | 'out'
    const [displayedSec, setDisplayedSec] = useState(null)
    const isSectionFadeRef = useRef(false)
    const fadeDuration = 400 // ms
    // Popup state now lives in global store
    const openPoiIndex = useGame((s) => s.openPoiIndex)
    const setOpenPoiIndex = useGame((s) => s.setOpenPoiIndex)
    const setSelectedPoiData = useGame((s) => s.setSelectedPoiData)
    const selectedSec = useGame((state) => state.selectedSec)
    const poiVisible = useGame((state) => state.poiVisible)

    // Load POI data from CSV on mount
    useEffect(() => {
        loadPOIData().then(data => setPoiDataMap(data))
    }, [])

    // Load offset JSON
    useEffect(() => {
        fetch('/POI_Markers_Offset.json')
            .then(r => r.json())
            .then(data => setOffsetMap(data))
            .catch(() => {})
    }, [])

    // SEC change: fade out current, then fade in new
    useEffect(() => {
        if (!poiVisible) {
            setDisplayedSec(null)
            setFadeState('in')
            return
        }
        if (displayedSec === null && selectedSec) {
            setDisplayedSec(selectedSec)
            setFadeState('in')
            return
        }
        if (selectedSec && displayedSec && selectedSec !== displayedSec) {
            isSectionFadeRef.current = true
            setFadeState('out')
            const timer = setTimeout(() => {
                setDisplayedSec(selectedSec)
                setFadeState('in')
            }, fadeDuration)
            return () => clearTimeout(timer)
        }
    }, [selectedSec, poiVisible])

    // Handle POI and SEC selection events
    useEffect(() => {
        const handlePoiSelected = (event) => {
            const poiName = event.detail?.poiName
            const fromWindow = event.detail?.fromWindow
            if (fromWindow && poiName) {
                const poiIndex = poiGroups.findIndex(poi => poi.name === poiName)
                if (poiIndex !== -1) {
                    setOpenPoiIndex(poiIndex)
                }
            }
        }
        const handlePoiDeselected = () => { setOpenPoiIndex(null) }
        const handleSecSelected = () => { setOpenPoiIndex(null) }

        window.addEventListener('poi-selected', handlePoiSelected)
        window.addEventListener('poi-deselected', handlePoiDeselected)
        window.addEventListener('sec-selected', handleSecSelected)
        return () => {
            window.removeEventListener('poi-selected', handlePoiSelected)
            window.removeEventListener('poi-deselected', handlePoiDeselected)
            window.removeEventListener('sec-selected', handleSecSelected)
        }
    }, [poiGroups, setOpenPoiIndex])

    // Initialize POI groups on mount — skip SEC006 POIs (handled by POI_Markers_SEC006)
    useEffect(() => {
        if (!nodes || !poiDataMap) return

        const poiGroupsList = []
        Object.entries(nodes).forEach(([nodeName, node]) => {
            if (!nodeName.includes('POI') && !nodeName.toUpperCase().includes('POI')) return

            // SEC006 POIs are owned by POI_Markers_SEC006
            const poiData = getPOIDataByNode(poiDataMap, nodeName)
            if (poiData?.secRef === 'SEC006') return

            poiNodesRef.current[nodeName] = node
            const worldPosition = new THREE.Vector3()
            if (node.position) worldPosition.copy(node.position)
            if (node.isMesh && node.geometry) {
                node.geometry.computeBoundingBox()
                const center = new THREE.Vector3()
                node.geometry.boundingBox.getCenter(center)
                worldPosition.add(center)
            }
            poiGroupsList.push({
                name: nodeName,
                node,
                worldPosition,
                isDefault: false,
            })
        })

        setPoiGroups(poiGroupsList)
    }, [nodes, poiDataMap])

    return (
        <>
            {poiGroups.map((poi, index) => {
                const isHovered = index === hoveredIndex
                const isOpen = index === openPoiIndex
                const poiData = poiDataMap ? getPOIDataByNode(poiDataMap, poi.name) : null
                if (!poiVisible) return null
                if (!displayedSec || poiData?.secRef !== displayedSec) return null

                // Resolve label position from offset JSON
                const poiKey = poi.name.match(/POI[_]?(\d+)/i)
                    ? `POI${poi.name.match(/POI[_]?(\d+)/i)[1].padStart(3, '0')}`
                    : poi.name
                const offsetEntry = offsetMap[poiKey]
                const labelPosition = offsetEntry && (offsetEntry.x !== 0 || offsetEntry.y !== 0 || offsetEntry.z !== 0)
                    ? new THREE.Vector3(
                        poi.worldPosition.x + offsetEntry.x,
                        poi.worldPosition.y + offsetEntry.y,
                        poi.worldPosition.z + offsetEntry.z
                      )
                    : poi.worldPosition

                let fadeClass = ''
                if (openPoiIndex === null && isSectionFadeRef.current) {
                    fadeClass = fadeState === 'in' ? 'poi-fade-in' : 'poi-fade-out'
                    if (fadeState === 'in') isSectionFadeRef.current = false
                }
                return (
                    <Html
                        zIndexRange={[0, 3500]}
                        wrapperClass="poi-html-portal"
                        key={`poi-${index}`}
                        position={labelPosition}
                        occlude={false}
                        center
                    >
                        <div
                            className={`textButtonContainer sec-marker-dynamic sec-solid ${isOpen ? 'sec006-poi-selected' : ''} ${fadeClass}`}
                            style={{
                                    transition: 'opacity 0.25s ease',
                                }}
                            onPointerOver={(e) => { e.stopPropagation(); setHoveredIndex(index) }}
                            onPointerOut={(e) => { e.stopPropagation(); setHoveredIndex((h) => (h === index ? null : h)) }}
                            onClick={(e) => {
                                e.stopPropagation()
                                if (openPoiIndex === index) return
                                track('marker_clicked', {
                                    poiName: poi.name,
                                    category: poiData?.category || 'Unknown',
                                    index: index
                                })
                                window.dispatchEvent(new CustomEvent('poi-selected', {
                                    detail: { poiName: poi.name, index: index, poiData }
                                }))
                                setSelectedPoiData(poiData || null)
                                setOpenPoiIndex(index)
                            }}
                        >
                            <div className={`sec-marker-wrapper ${isOpen ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}>
                                <span className="sec-label textButton">
                                    {poiData?.category || poi.name}
                                </span>
                                <div className="textButtonArrow arrowButton" />
                            </div>
                        </div>
                    </Html>
                )
            })}
        </>
    )
}

// POI controls moved to UI.jsx
